/* The Summary: one row per report per month (January up to the chosen month), with the line items as columns.
 * By-site figures are summed over the report's sites. Built from the same layout rows the report view uses, so labels and order match. */
import S from './core.js';
import { statusOf } from './approvals.js';
import { MONTH_NAMES, periodOf } from './periods.js';

const BY_SITE_LABELS = { total_power_usage_kwh_by_site: 'Total Power Usage (kWh)', power_revenue_gbp_by_site: 'Power Revenue (£)' };
const SECTION_TITLES = { site_detail: 'Site, chargepoint & socket detail', financials: 'Financials (£)', by_site: 'By site - summed over all sites' };
const SECTION_ORDER = ['site_detail', 'financials', 'by_site'];

/** Keep the latest as-at version of each report id for the year. */
export function latestPerReport(list, year) {
  const best = new Map();
  list.filter((r) => r.report_year === year).forEach((r) => {
    const cur = best.get(r.settlement_report_id);
    if (!cur || r.as_at_date > cur.as_at_date) best.set(r.settlement_report_id, r);
  });
  return [...best.values()].sort((a, b) => a.settlement_report_id.localeCompare(b.settlement_report_id));
}

/** entries: [{ report, figures }]; approvals: report_approval rows.
 * -> { year, monthIdx, month, title, columns: [{ key, section, label, kind }], groups: [{ section, title, span }], rows: [...] }
 * Each row: { key, reportKey, id, partner, manager, monthIdx, month, values: { [columnKey]: number|null }, finance, account_manager }.
 * A month in which a report has no figures at all (it runs only to its as-at date) gets no row. */
export function buildSummary(entries, year, monthIdx, approvals) {
  const master = [];                                   // ordered column keys across every report
  const meta = new Map();                              // key -> { key, section, label, kind }
  const perReport = entries.map(({ report, figures }) => {
    const values = new Map();                          // key -> 12 monthly values (null = none)
    const order = [];
    S.buildLayout(report, figures).pages.forEach((pg) => pg.rows.forEach((row) => {
      if (row.k !== 'grid') return;
      const key = `${row.section}|${row.code}`;
      if (!values.has(key)) { values.set(key, new Array(12).fill(null)); order.push(key); }
      const months = values.get(key);
      for (let mo = 0; mo <= monthIdx; mo++) {
        const cell = row.cells[mo];
        if (cell && cell.v !== null) months[mo] = (months[mo] || 0) + cell.v;   // by_site rows: one per site, so this sums the sites
      }
      if (!meta.has(key)) {
        meta.set(key, { key, section: row.section, kind: row.kind,
          label: row.section === 'by_site' ? `${BY_SITE_LABELS[row.code] || row.code.replace(/_/g, ' ')} - all sites` : row.label });
      }
    }));
    let at = -1;                                       // merge this report's column order into the master order
    order.forEach((key) => {
      let i = master.indexOf(key);
      if (i < 0) { i = at + 1; master.splice(i, 0, key); }
      at = i;
    });
    return { report, values };
  });

  const columns = master.map((k) => meta.get(k)).map((c, i) => ({ c, i }))
    .sort((a, b) => (SECTION_ORDER.indexOf(a.c.section) - SECTION_ORDER.indexOf(b.c.section)) || (a.i - b.i)).map((x) => x.c);
  const groups = [];
  columns.forEach((c) => {
    const last = groups[groups.length - 1];
    if (last && last.section === c.section) last.span += 1;
    else groups.push({ section: c.section, title: SECTION_TITLES[c.section] || c.section, span: 1 });
  });

  const rows = [];
  perReport.forEach(({ report, values }) => {
    const reportKey = `${report.settlement_report_id}/${report.report_year}/${report.as_at_date}`;
    for (let mo = 0; mo <= monthIdx; mo++) {
      const row = {};
      let any = false;
      columns.forEach((c) => {
        const v = values.has(c.key) ? values.get(c.key)[mo] : null;
        row[c.key] = v;
        if (v !== null) any = true;
      });
      if (!any) continue;
      const status = statusOf(approvals, report, periodOf({ mode: 'monthly', year, month: mo }));
      rows.push({ key: `${reportKey}/${mo}`, reportKey, id: report.settlement_report_id, partner: report.client_name || report.company_name || '',
        manager: report.account_manager || '', monthIdx: mo, month: MONTH_NAMES[mo], values: row, finance: status.finance, account_manager: status.account_manager });
    }
  });
  return { year, monthIdx, month: MONTH_NAMES[monthIdx], title: monthIdx === 11 ? `Summary - ${year}` : `Summary - January to ${MONTH_NAMES[monthIdx]} ${year}`, columns, groups, rows };
}

/** Narrow the rows by partner name and report id (both: contains, ignoring case) and by one month ('all' or 0-11). */
export function filterRows(rows, { partner = '', reportId = '', month = 'all' } = {}) {
  const p = partner.trim().toLowerCase(), id = reportId.trim().toLowerCase();
  return rows.filter((r) => (!p || r.partner.toLowerCase().includes(p)) && (!id || r.id.toLowerCase().includes(id)) && (month === 'all' || r.monthIdx === Number(month)));
}

/** Column totals over the rows shown: money and by-site columns always; percentages never; the site/chargepoint/socket counts
 * only when every row is the same month (adding a count across months would be meaningless). -> { [columnKey]: number|null } */
export function totalsOf(columns, rows) {
  const oneMonth = new Set(rows.map((r) => r.monthIdx)).size <= 1;
  const out = {};
  columns.forEach((c) => {
    const addable = c.kind !== 'pct' && (c.section !== 'site_detail' || oneMonth);
    const present = addable ? rows.map((r) => r.values[c.key]).filter((v) => v !== null && v !== undefined) : [];
    out[c.key] = present.length ? present.reduce((a, b) => a + b, 0) : null;
  });
  return out;
}

export const fmt = (kind, v) => (v === null || v === undefined ? '' : S.fmtNum(kind, kind === 'money' ? Math.round((v + Number.EPSILON) * 100) / 100 : v));
